// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Tooltip } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DnsIcon from '@mui/icons-material/Dns';
import UsbIcon from '@mui/icons-material/Usb';
import BadgeIcon from '@mui/icons-material/Badge';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SendIcon from '@mui/icons-material/Send';
import { useAuthStore } from '../store';
import { GLASS } from '../theme';
import NotificationBell from './NotificationBell';

// Tout ce qui concerne un client (sites, chambres, serveurs, comptes) se lit
// sur sa fiche, d'où une seule entrée « Clients ». Les adresses /etablissements,
// /properties, /kits et /fleet redirigent (App.jsx).
const NAV_ITEMS = [
  { to: '/home', label: 'Tableau de bord', icon: <DashboardIcon fontSize="small" /> },
  { to: '/clients', label: 'Clients', icon: <PeopleIcon fontSize="small" /> },
  // Réservations du kit et demandes d'intérêt des particuliers venues du site.
  { to: '/reservations', label: 'Réservations', icon: <ShoppingCartIcon fontSize="small" /> },
  // Présentation envoyée par courriel aux établissements démarchés.
  { to: '/prospection', label: 'Prospection', icon: <SendIcon fontSize="small" /> },
  { to: '/serveurs', label: 'Serveurs', icon: <DnsIcon fontSize="small" /> },
  // Une box de foyer s'active par un QR scanné par la famille ; un serveur
  // d'établissement se pose depuis une clé gravée à l'atelier. Deux gestes
  // distincts, deux entrées distinctes.
  { to: '/cles-de-pose', label: 'Clés de pose', icon: <UsbIcon fontSize="small" /> },
  // Identité des poseurs, pas des machines. Ces comptes valent chez tous les
  // clients à la fois, ce qu'aucun autre compte du produit ne fait.
  { to: '/comptes-de-pose', label: 'Comptes de pose', icon: <BadgeIcon fontSize="small" /> },
  { to: '/facturation', label: 'Facturation', icon: <ReceiptLongIcon fontSize="small" /> },
];

// La console de contrats est une page statique servie par nginx à la même
// origine, hors du routeur React : un lien classique y mène. Elle n'a pas
// d'écran de connexion et lit la session posée ici (`access_token` du
// localStorage, commun à la même origine, y compris dans un nouvel onglet).
// Sans session valide, elle renvoie vers /login.
const CONTRACTS_URL = '/contrats.html';

// Sur le fond sombre, l'opacité ne distingue pas assez une entrée active d'une
// entrée au repos : deux couleurs franches de la charte, l'encre pour l'entrée
// courante, l'ardoise pour les autres, et le bleu ciel pour le soulignement.
const ACTIVE = 'text.primary';
const IDLE = 'text.secondary';
const UNDERLINE = 'secondary.main';

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((state) => state.token);
  const logout = useAuthStore((state) => state.logout);

  // On attend la révocation côté serveur (bornée dans le temps, jamais en
  // échec) avant de quitter l'écran : partir tout de suite laisserait la page
  // de connexion s'afficher alors que le jeton est encore valable.
  const [sortie, setSortie] = React.useState(false);
  const handleLogout = async () => {
    if (sortie) return;
    setSortie(true);
    try {
      await logout();
    } finally {
      // La barre reste montée : sans cette remise à zéro, le bouton resterait
      // inerte à la connexion suivante.
      setSortie(false);
      navigate('/login');
    }
  };

  if (!token) return null;

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography
          variant="h6"
          sx={{ mr: 2, flexShrink: 0, whiteSpace: 'nowrap', color: ACTIVE, letterSpacing: '.4px' }}
        >
          Vigeos
        </Typography>
        {/* Les entrées défilent horizontalement : sur un portable, les actions
            de droite (contrats, cloche, réglages, déconnexion) occupent la
            moitié de la barre. Ces actions ne défilent pas, pour que la
            déconnexion reste toujours accessible. */}
        <Box
          sx={{
            display: 'flex',
            gap: 0.25,
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.to ||
              (item.to !== '/home' && location.pathname.startsWith(item.to));
            return (
              <Button
                key={item.to}
                color="inherit"
                component={Link}
                to={item.to}
                startIcon={item.icon}
                sx={{
                  color: active ? ACTIVE : IDLE,
                  borderBottom: '2px solid',
                  borderBottomColor: active ? UNDERLINE : 'transparent',
                  borderRadius: 0,
                  px: 1,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  '&:hover': { color: ACTIVE, backgroundColor: GLASS },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>
        <Button
          color="inherit"
          href={CONTRACTS_URL}
          startIcon={<DescriptionIcon fontSize="small" />}
          sx={{
            color: IDLE,
            borderRadius: 0,
            px: 1,
            ml: 1,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '&:hover': { color: ACTIVE, backgroundColor: GLASS },
          }}
        >
          Contrats
        </Button>
        <NotificationBell />
        <Tooltip title="Paramètres">
          <IconButton
            color="inherit"
            component={Link}
            to="/settings"
            sx={{ color: location.pathname === '/settings' ? ACTIVE : IDLE, ml: 0.5 }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Button
          color="inherit"
          onClick={handleLogout}
          disabled={sortie}
          sx={{
            color: IDLE,
            ml: 0.5,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            '&:hover': { color: ACTIVE, backgroundColor: GLASS },
          }}
        >
          Déconnexion
        </Button>
      </Toolbar>
    </AppBar>
  );
}
